import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  Lock,
  LayoutDashboard,
  Users,
  Shuffle,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Loader2,
  Trophy,
  Copy,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Gift,
  DollarSign,
  AlertTriangle,
  ChevronDown,
  Eye,
  EyeOff,
  QrCode,
  Download,
  Share2
} from "lucide-react";
import { toast } from "sonner";
import SorteioRoleta from "./SorteioRoleta";
import ContentTab from "./ContentTab";


const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Escapa texto antes de interpolar em HTML montado como string (janela de impressão).
const escHtml = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Tabs
const TABS = [
  { id: "campanhas", label: "Campanhas", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Users },
  { id: "sorteio", label: "Sorteio", icon: Shuffle },
  { id: "conteudo", label: "Conteúdo do Site", icon: Edit3 },
];

export default function AdminPanel() {
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [activeTab, setActiveTab] = useState("campanhas");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passwordInput.trim()) { toast.error("Digite a senha."); return; }
    setAuthLoading(true);
    try {
      // Verifica a senha tentando buscar campanhas
      await axios.get(`${API}/admin/campanhas`, {
        headers: { "x-admin-password": passwordInput }
      });
      setAdminPassword(passwordInput);
      setAuthenticated(true);
      toast.success("Acesso liberado!");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Senha incorreta.");
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Erro ao conectar ao servidor.");
      }
    } finally {
      setAuthLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-card-gradient border border-border/60 shadow-gold rounded-2xl p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 ring-2 ring-primary/30 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-primary" />
            </div>
            <h1 className="font-display text-2xl font-light">Painel Administrativo</h1>
            <p className="text-muted-foreground text-sm mt-1">Gestão de campanhas e sorteios</p>
          </div>
          <form id="form-login-admin" onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                Senha de Acesso
              </label>
              <div className="relative">
                <input
                  id="input-senha-admin"
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Digite a senha..."
                  autoFocus
                  className="w-full px-4 pr-12 py-3.5 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              id="btn-entrar-admin"
              type="submit"
              disabled={authLoading}
              className="w-full py-3.5 rounded-full font-medium text-base transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
            >
              {authLoading ? <><Loader2 className="animate-spin" size={18} /> Verificando...</> : "Entrar"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container max-w-6xl h-16 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <span className="text-gold-gradient font-display text-xl font-bold tracking-tight">Juliano Wagner</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-muted-foreground border-l border-border/80 pl-2">
              Painel Admin
            </span>
          </div>
          <button
            onClick={() => { setAuthenticated(false); setAdminPassword(""); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <Lock size={12} /> Sair
          </button>
        </div>

        {/* Tabs */}
        <div className="border-t border-border/40">
          <div className="container max-w-6xl px-4">
            <nav className="flex gap-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    id={`tab-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-6xl px-4 py-8">
        <AnimatePresence mode="wait">
          {activeTab === "campanhas" && (
            <CampanhasTab key="campanhas" adminPassword={adminPassword} />
          )}
          {activeTab === "leads" && (
            <LeadsTab key="leads" adminPassword={adminPassword} />
          )}
          {activeTab === "sorteio" && (
            <SorteioTab key="sorteio" adminPassword={adminPassword} />
          )}
          {activeTab === "conteudo" && (
            <motion.div key="conteudo" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
              <ContentTab adminPassword={adminPassword} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

// ──────────────────────────────────────────────
// TAB: CAMPANHAS
// ──────────────────────────────────────────────
function CampanhasTab({ adminPassword }) {
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrCampanha, setQrCampanha] = useState(null); // campanha cujo QR está aberto
  const [form, setForm] = useState({
    slug: "", nome_parceiro: "", premio: "", descricao: "", ativo: true, comissao_por_lead: "",
    data_inicio: "", data_fim: ""
  });

  const fetchCampanhas = useCallback(async () => {
    const headers = { "x-admin-password": adminPassword };
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/campanhas`, { headers });
      setCampanhas(res.data);
    } catch { toast.error("Erro ao carregar campanhas."); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { fetchCampanhas(); }, [fetchCampanhas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.nome_parceiro || !form.premio) {
      toast.error("Preencha os campos obrigatórios: Slug, Nome do Parceiro e Prêmio.");
      return;
    }
    setSaving(true);
    const headers = { "x-admin-password": adminPassword };
    try {
      const payload = {
        ...form,
        comissao_por_lead: parseFloat(form.comissao_por_lead) || 0,
        data_inicio: form.data_inicio ? new Date(form.data_inicio).toISOString() : null,
        data_fim: form.data_fim ? new Date(form.data_fim + "T23:59:59").toISOString() : null,
      };
      if (editingId) {
        await axios.patch(`${API}/admin/campanhas/${editingId}`, payload, { headers });
        toast.success("Campanha atualizada!");
      } else {
        await axios.post(`${API}/admin/campanhas`, payload, { headers });
        toast.success("Campanha criada!");
      }
      setForm({ slug: "", nome_parceiro: "", premio: "", descricao: "", ativo: true, comissao_por_lead: "", data_inicio: "", data_fim: "" });
      setShowForm(false);
      setEditingId(null);
      fetchCampanhas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar campanha.");
    } finally { setSaving(false); }
  };

  const handleEdit = (c) => {
    setForm({
      slug: c.slug,
      nome_parceiro: c.nome_parceiro,
      premio: c.premio,
      descricao: c.descricao || "",
      ativo: c.ativo,
      comissao_por_lead: c.comissao_por_lead?.toString() || "0",
      data_inicio: c.data_inicio ? c.data_inicio.substring(0, 10) : "",
      data_fim: c.data_fim ? c.data_fim.substring(0, 10) : "",
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta campanha?")) return;
    const headers = { "x-admin-password": adminPassword };
    try {
      await axios.delete(`${API}/admin/campanhas/${id}`, { headers });
      toast.success("Campanha excluída.");
      fetchCampanhas();
    } catch { toast.error("Erro ao excluir campanha."); }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/parceiro/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const handleExportCSV = async (slug, partnerName) => {
    const toastId = toast.loading(`Gerando CSV dos leads de "${partnerName || slug}"...`);
    try {
      const headers = { "x-admin-password": adminPassword };
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers,
        params: { partner_slug: slug },
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_${slug}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Leads de "${partnerName || slug}" exportados em CSV (;)!`, { id: toastId });
    } catch {
      toast.error("Erro ao exportar leads deste parceiro.", { id: toastId });
    }
  };

  const slugify = (text) =>
    text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-light">Campanhas de Parceiros</h2>
          <p className="text-muted-foreground text-sm mt-1">Gerencie links de captura por parceiro</p>
        </div>
        <button
          id="btn-nova-campanha"
          onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ slug: "", nome_parceiro: "", premio: "", descricao: "", ativo: true, comissao_por_lead: "", data_inicio: "", data_fim: "" }); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all shadow-gold hover:translate-y-[-1px]"
          style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
        >
          <Plus size={16} /> Nova Campanha
        </button>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-card-gradient border border-primary/30 rounded-2xl p-6 mb-6 overflow-hidden"
          >
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              {editingId ? <Edit3 size={16} /> : <Plus size={16} />}
              {editingId ? "Editar Campanha" : "Nova Campanha"}
            </h3>
            <form id="form-campanha" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Slug (URL) <span className="text-primary">*</span>
                </label>
                <input
                  id="input-slug"
                  type="text"
                  required
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: slugify(e.target.value) }))}
                  placeholder="ex: barbearia-silva"
                  disabled={!!editingId}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-mono text-sm disabled:opacity-50"
                />
                {form.slug && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    URL: <code className="text-primary">/parceiro/{form.slug}</code>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Nome do Parceiro <span className="text-primary">*</span>
                </label>
                <input
                  id="input-nome-parceiro-admin"
                  type="text"
                  required
                  value={form.nome_parceiro}
                  onChange={(e) => setForm((p) => ({ ...p, nome_parceiro: e.target.value }))}
                  placeholder="ex: Barbearia Silva"
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Prêmio / Isca <span className="text-primary">*</span>
                </label>
                <input
                  id="input-premio"
                  type="text"
                  required
                  value={form.premio}
                  onChange={(e) => setForm((p) => ({ ...p, premio: e.target.value }))}
                  placeholder="ex: R$ 200 em cortes de cabelo"
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Descrição (opcional)
                </label>
                <textarea
                  id="input-descricao"
                  rows={2}
                  value={form.descricao}
                  onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
                  placeholder="Texto complementar exibido na página do parceiro..."
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Comissão por Lead (R$)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                    <DollarSign size={16} />
                  </div>
                  <input
                    id="input-comissao"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.comissao_por_lead}
                    onChange={(e) => setForm((p) => ({ ...p, comissao_por_lead: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                  />
                </div>
              </div>
              {/* Período da campanha */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Data Início
                </label>
                <input
                  id="input-data-inicio"
                  type="date"
                  value={form.data_inicio}
                  onChange={(e) => setForm((p) => ({ ...p, data_inicio: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Data Fim
                </label>
                <input
                  id="input-data-fim"
                  type="date"
                  value={form.data_fim}
                  min={form.data_inicio || undefined}
                  onChange={(e) => setForm((p) => ({ ...p, data_fim: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-sm"
                />
              </div>

              <div className="flex items-end pb-1">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, ativo: !p.ativo }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                    form.ativo ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground"
                  }`}
                >
                  {form.ativo ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  Campanha {form.ativo ? "Ativa" : "Inativa"}
                </button>
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button
                  id="btn-salvar-campanha"
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-60"
                  style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                >
                  {saving ? <><Loader2 size={16} className="animate-spin" /> Salvando...</> : <><Check size={16} /> {editingId ? "Salvar Alterações" : "Criar Campanha"}</>}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium border border-border hover:bg-white/5 transition-colors text-muted-foreground"
                >
                  <X size={16} /> Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      ) : campanhas.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl">
          <Gift size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhuma campanha cadastrada ainda.</p>
          <p className="text-xs text-muted-foreground mt-1">Clique em "Nova Campanha" para começar.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campanhas.map((c) => (
            <div
              key={c.id}
              className="bg-card-gradient border border-border/60 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`inline-block w-2 h-2 rounded-full ${c.ativo ? "bg-green-500" : "bg-muted-foreground"}`} />
                  <h3 className="font-medium text-foreground">{c.nome_parceiro}</h3>
                  <span className="text-[10px] font-mono text-muted-foreground bg-white/5 border border-border px-2 py-0.5 rounded-full">
                    /{c.slug}
                  </span>
                  {/* Badge de período */}
                  {(() => {
                    const now = new Date();
                    const fim = c.data_fim ? new Date(c.data_fim) : null;
                    const inicio = c.data_inicio ? new Date(c.data_inicio) : null;
                    if (fim && now > fim) return (
                      <span className="text-[10px] bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full font-medium">
                        Encerrada
                      </span>
                    );
                    if (inicio && now < inicio) return (
                      <span className="text-[10px] bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full font-medium">
                        Aguardando início
                      </span>
                    );
                    if (fim) return (
                      <span className="text-[10px] bg-green-500/15 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full font-medium">
                        Até {fim.toLocaleDateString("pt-BR")}
                      </span>
                    );
                    return null;
                  })()}
                </div>
                <p className="text-sm text-primary flex items-center gap-1">
                  <Gift size={13} /> {c.premio}
                </p>
                {c.descricao && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{c.descricao}</p>
                )}
                {c.comissao_por_lead > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Comissão: R$ {Number(c.comissao_por_lead).toFixed(2)}/lead
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => copyLink(c.slug)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Copiar link"
                >
                  <Copy size={14} /> Copiar Link
                </button>
                <button
                  onClick={() => setQrCampanha(c)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 hover:bg-primary/10 transition-colors text-xs text-primary hover:text-primary font-medium cursor-pointer"
                  title="Ver QR Code"
                >
                  <QrCode size={14} /> QR Code
                </button>
                <button
                  onClick={() => handleExportCSV(c.slug, c.nome_parceiro)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:bg-emerald-500/10 transition-colors text-xs text-emerald-400 hover:text-emerald-300 font-medium cursor-pointer"
                  title="Exportar todos os leads deste parceiro em CSV (;)"
                >
                  <Download size={14} /> Exportar CSV (;)
                </button>
                <a
                  href={`/parceiro/${c.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Abrir link"
                >
                  <ExternalLink size={14} /> Abrir
                </a>
                <button
                  onClick={() => handleEdit(c)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Editar"
                >
                  <Edit3 size={14} /> Editar
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive/30 hover:bg-destructive/10 transition-colors text-xs text-destructive/70 hover:text-destructive"
                  title="Excluir"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      <AnimatePresence>
        {qrCampanha && (
          <QrModal campanha={qrCampanha} onClose={() => setQrCampanha(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ──────────────────────────────────────────────
// TAB: LEADS
// ──────────────────────────────────────────────
function LeadsTab({ adminPassword }) {
  const [leads, setLeads] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterSlug, setFilterSlug] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchLeads = useCallback(async (slug) => {
    const headers = { "x-admin-password": adminPassword };
    setLoading(true);
    try {
      const params = slug ? { partner_slug: slug } : {};
      const [leadsRes, campanhasRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers, params }),
        axios.get(`${API}/admin/campanhas`, { headers })
      ]);
      setLeads(leadsRes.data);
      setCampanhas(campanhasRes.data);
    } catch { toast.error("Erro ao carregar leads."); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { fetchLeads(filterSlug); }, [fetchLeads, filterSlug]);

  const handleExportLeadsCSV = async () => {
    setExporting(true);
    const toastId = toast.loading("Gerando arquivo CSV delimitado por ponto e vírgula (;)...");
    try {
      const headers = { "x-admin-password": adminPassword };
      const params = filterSlug ? { partner_slug: filterSlug } : {};
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers,
        params,
        responseType: "blob"
      });

      let fileName = filterSlug ? `leads_${filterSlug}.csv` : "leads_todos.csv";
      const disposition = res.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) fileName = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(
        filterSlug
          ? `CSV dos leads do parceiro exportado com sucesso!`
          : "CSV de todos os leads exportado com sucesso!",
        { id: toastId }
      );
    } catch {
      toast.error("Erro ao exportar leads para CSV.", { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteLead = async (id, name) => {
    if (!window.confirm(`Excluir o lead de "${name}"? Esta ação não pode ser desfeita.`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: { "x-admin-password": adminPassword }
      });
      toast.success("Lead excluído.");
      fetchLeads(filterSlug);
    } catch {
      toast.error("Erro ao excluir lead.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-light">Leads Capturados</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {leads.length} lead{leads.length !== 1 ? "s" : ""} {filterSlug ? `do parceiro "${filterSlug}"` : "no total"}
            <span className="ml-2 text-xs text-emerald-400/90 font-mono hidden sm:inline">
              · CSV (;) com UTF-8 pronto para SDR de IA e Excel
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select
              id="filtro-parceiro"
              value={filterSlug}
              onChange={(e) => setFilterSlug(e.target.value)}
              className="appearance-none pr-8 pl-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:border-primary/70 cursor-pointer"
            >
              <option value="">Todos os parceiros</option>
              {campanhas.map((c) => (
                <option key={c.id} value={c.slug}>{c.nome_parceiro}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          <button
            id="btn-exportar-csv-leads"
            onClick={handleExportLeadsCSV}
            disabled={leads.length === 0 || exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar leads em CSV delimitado por ponto e vírgula (;)"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            <span>Exportar CSV (;)</span>
          </button>

          <button
            onClick={() => fetchLeads(filterSlug)}
            className="p-2.5 rounded-xl border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
            title="Atualizar"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl">
          <Users size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhum lead encontrado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-white/[0.02]">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium">Nome</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium">WhatsApp</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium hidden sm:table-cell">E-mail</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium hidden md:table-cell">Parceiro</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium hidden lg:table-cell">Diagnóstico / SDR</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium hidden xl:table-cell">Data</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{lead.name}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono">{lead.phone}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell truncate max-w-[180px]">{lead.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {lead.partner_slug ? (
                      <span className="inline-block text-xs font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full" title={lead.nome_parceiro}>
                        {lead.nome_parceiro || lead.partner_slug}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">{lead.source || "site"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell max-w-xs truncate" title={lead.message || "Sem diagnóstico"}>
                    {lead.message ? lead.message.replace(/\n+/g, " · ") : <span className="opacity-40">-</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden xl:table-cell whitespace-nowrap">
                    {formatDate(lead.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDeleteLead(lead.id, lead.name)}
                      disabled={deletingId === lead.id}
                      className="p-1.5 rounded-lg text-destructive/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40 cursor-pointer"
                      title="Excluir lead"
                    >
                      {deletingId === lead.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

// ──────────────────────────────────────────────
// TAB: SORTEIO
// ──────────────────────────────────────────────
function SorteioTab({ adminPassword }) {
  const [campanhas, setCampanhas] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");

  useEffect(() => {
    const headers = { "x-admin-password": adminPassword };
    axios.get(`${API}/admin/campanhas`, { headers })
      .then((res) => setCampanhas(res.data))
      .catch(() => toast.error("Erro ao carregar campanhas."));
  }, [adminPassword]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <SorteioRoleta
        adminPassword={adminPassword}
        campanhas={campanhas}
        selectedSlug={selectedSlug}
        setSelectedSlug={setSelectedSlug}
      />
    </motion.div>
  );
}

// ──────────────────────────────────────────────
// MODAL: QR CODE
// ──────────────────────────────────────────────
function QrModal({ campanha, onClose }) {
  const slug = campanha.slug;
  const url = `${window.location.origin}/parceiro/${slug}`;

  const handleDownload = () => {
    const qrCanvas = document.getElementById(`qr-canvas-${slug}`);
    if (!qrCanvas) return;

    // Dimensões do card final
    const W = 500;
    const padding = 40;
    const qrSize = 240;

    // Calcula altura dinâmica baseada no conteúdo
    const hasDesc = campanha.descricao && campanha.descricao.trim();
    const H = hasDesc ? 560 : 480;

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Fundo branco
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);

    // Borda dourada arredondada
    ctx.strokeStyle = "#d4af37";
    ctx.lineWidth = 3;
    const r = 16;
    ctx.beginPath();
    ctx.moveTo(r, 0); ctx.lineTo(W - r, 0);
    ctx.quadraticCurveTo(W, 0, W, r);
    ctx.lineTo(W, H - r); ctx.quadraticCurveTo(W, H, W - r, H);
    ctx.lineTo(r, H); ctx.quadraticCurveTo(0, H, 0, H - r);
    ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.stroke();

    let y = padding;

    // Nome do parceiro
    ctx.fillStyle = "#111111";
    ctx.font = "bold 22px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(campanha.nome_parceiro, W / 2, y + 22);
    y += 42;

    // Prêmio
    ctx.fillStyle = "#d4af37";
    ctx.font = "15px Arial, sans-serif";
    ctx.fillText(`🏆 ${campanha.premio}`, W / 2, y + 16);
    y += 36;

    // QR Code centralizado
    const qrX = (W - qrSize) / 2;
    ctx.drawImage(qrCanvas, qrX, y, qrSize, qrSize);
    y += qrSize + 20;

    // Descrição (se houver) — com quebra de linha automática
    if (hasDesc) {
      ctx.fillStyle = "#555555";
      ctx.font = "13px Arial, sans-serif";
      const maxW = W - padding * 2;
      const words = campanha.descricao.split(" ");
      let line = "";
      const lineHeight = 20;
      for (let i = 0; i < words.length; i++) {
        const test = line + words[i] + " ";
        if (ctx.measureText(test).width > maxW && i > 0) {
          ctx.fillText(line.trim(), W / 2, y);
          line = words[i] + " ";
          y += lineHeight;
        } else {
          line = test;
        }
      }
      if (line.trim()) { ctx.fillText(line.trim(), W / 2, y); y += lineHeight; }
      y += 12;
    }

    // URL
    ctx.fillStyle = "#888888";
    ctx.font = "11px Arial, sans-serif";
    const displayUrl = `${window.location.origin}/parceiro/${slug}`;
    ctx.fillText(displayUrl, W / 2, y + 12);
    y += 32;

    // CTA
    ctx.fillStyle = "#111111";
    ctx.font = "bold 14px Arial, sans-serif";
    ctx.fillText("Aponte a câmera do celular para participar!", W / 2, y);

    // Download
    const link = document.createElement("a");
    link.download = `qrcode-${slug}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR Code com descrição baixado!");
  };


  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Acesse: /parceiro/${slug}`, url });
        toast.success("Link compartilhado!");
      } catch {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const canvas = document.getElementById(`qr-canvas-${slug}`);
    const qrDataUrl = canvas ? canvas.toDataURL("image/png") : "";
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>QR Code — ${escHtml(campanha.nome_parceiro)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; background: #fff; color: #111;
                 display: flex; flex-direction: column; align-items: center;
                 justify-content: center; min-height: 100vh; padding: 40px; }
          .card { border: 2px solid #d4af37; border-radius: 16px; padding: 40px;
                  text-align: center; max-width: 420px; width: 100%; }
          .parceiro { font-size: 22px; font-weight: bold; color: #111; margin-bottom: 4px; }
          .premio { font-size: 15px; color: #d4af37; margin-bottom: 16px; }
          .descricao { font-size: 13px; color: #555; margin-bottom: 20px;
                       line-height: 1.6; border-top: 1px solid #eee; padding-top: 16px; }
          .qr { margin: 0 auto 20px auto; }
          .qr img { width: 220px; height: 220px; }
          .url { font-size: 11px; color: #888; word-break: break-all;
                 background: #f5f5f5; padding: 8px 12px; border-radius: 8px; }
          .cta { font-size: 14px; color: #111; margin-top: 20px; font-weight: 500; }
          .regras-title { font-size: 10px; font-weight: bold; letter-spacing: 0.12em;
                          text-transform: uppercase; color: #888; margin: 20px 0 8px 0;
                          border-top: 1px solid #eee; padding-top: 16px; }
          .regras-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; text-align: left; }
          .regra { background: #f9f9f9; border: 1px solid #e5e5e5; border-radius: 8px;
                   padding: 8px 10px; }
          .regra-num { font-size: 9px; font-weight: bold; color: #d4af37;
                       text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2px; }
          .regra-title { font-size: 11px; font-weight: bold; color: #111; margin-bottom: 2px; }
          .regra-text { font-size: 10px; color: #666; line-height: 1.4; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="parceiro">${escHtml(campanha.nome_parceiro)}</div>
          <div class="premio">🏆 ${escHtml(campanha.premio)}</div>
          <div class="qr"><img src="${qrDataUrl}" alt="QR Code" /></div>
          ${campanha.descricao ? `<div class="descricao">${escHtml(campanha.descricao)}</div>` : ""}
          <div class="url">${escHtml(url)}</div>
          <div class="cta">Aponte a câmera do celular para participar!</div>
          <div class="regras-title">🛡️ Regulamento da Promoção</div>
          <div class="regras-grid">
            <div class="regra">
              <div class="regra-num">Regra 01</div>
              <div class="regra-title">Participação Única</div>
              <div class="regra-text">Só é permitida 1 participação por número de WhatsApp. Duplicatas não serão contabilizadas.</div>
            </div>
            <div class="regra">
              <div class="regra-num">Regra 02</div>
              <div class="regra-title">Dados Válidos</div>
              <div class="regra-text">Preencha com dados reais. O contato com o vencedor será feito exclusivamente pelo telefone cadastrado.</div>
            </div>
            <div class="regra">
              <div class="regra-num">Regra 03</div>
              <div class="regra-title">Apenas no Local</div>
              <div class="regra-text">Ação exclusiva para clientes presenciais. Links compartilhados em grupos serão desclassificados.</div>
            </div>
            <div class="regra">
              <div class="regra-num">Regra 04</div>
              <div class="regra-title">Resgate do Prêmio</div>
              <div class="regra-text">O vencedor deverá comprovar a titularidade e terá 7 dias corridos para retirar a premiação.</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", bounce: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card-gradient border border-primary/30 shadow-gold rounded-2xl p-8 w-full max-w-sm text-center relative"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-1">
          <QrCode size={18} className="text-primary" />
          <h3 className="font-display text-xl font-light">QR Code do Parceiro</h3>
        </div>
        <p className="text-sm font-medium text-foreground mb-0.5">{campanha.nome_parceiro}</p>
        <p className="text-xs text-primary mb-1">🏆 {campanha.premio}</p>
        {campanha.descricao && (
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{campanha.descricao}</p>
        )}
        <p className="text-xs text-muted-foreground mb-5">
          <code className="text-primary font-mono">/parceiro/{slug}</code>
        </p>

        {/* QR Code — fundo branco para leitura garantida */}
        <div className="inline-block p-4 rounded-2xl bg-white mx-auto mb-5 shadow-inner">
          <QRCodeCanvas
            id={`qr-canvas-${slug}`}
            value={url}
            size={220}
            bgColor="#ffffff"
            fgColor="#0f0d0a"
            level="H"
            includeMargin={false}
          />
        </div>

        {/* URL legível */}
        <div className="bg-background/60 border border-border rounded-xl px-4 py-2 mb-5 text-xs font-mono text-muted-foreground break-all">
          {url}
        </div>

        {/* Botões */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button
            id={`btn-download-qr-${slug}`}
            onClick={handleDownload}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
          >
            <Download size={18} />
            Baixar PNG
          </button>
          <button
            onClick={handleCopyLink}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
          >
            <Copy size={18} />
            Copiar Link
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleShare}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-border hover:bg-white/5 transition-colors text-xs text-muted-foreground hover:text-foreground"
          >
            <Share2 size={18} />
            Compartilhar
          </button>
          <button
            id={`btn-print-regras-${slug}`}
            onClick={handlePrint}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-primary/40 hover:bg-primary/10 transition-colors text-xs text-primary font-medium"
          >
            <QrCode size={18} />
            Imprimir + Regras
          </button>
        </div>

        <p className="text-[11px] text-muted-foreground mt-5 leading-relaxed">
          Baixe e imprima o QR Code para colocar no estabelecimento do parceiro.
          O cliente aponta o celular e é direcionado direto para o formulário.
        </p>
      </motion.div>
    </motion.div>
  );
}

